import React from 'react';
import { Link } from 'react-router';
import GlassContainer from 'components/GlassContainer/GlassContainer';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import { Points } from 'components/Points/Points';
import { Activity } from 'types/interfaces/activity';
import { formatRelativeTime } from 'utils/relativeTime';
import { ActivityUserGroup } from '../groupActivity';
import ActivityFeedItem from './ActivityFeedItem';
import './ActivityTradeGroup.css';

interface ActivityTradeGroupProps {
  group: ActivityUserGroup;
  /** CSS width (px) for each compact tile — driven by the S/M/L view mode. */
  tileWidth: number;
  /** Size of each card-value Points component — driven by the S/M/L view mode. */
  valueSize: 'big' | 'small';
  onSend?: (activity: Activity) => void;
}

/**
 * "By trader" view: every activity item from one user in a single container. The user
 * identity (avatar/flag/username/balance) is shown once as a header, followed by a summary
 * (item count + total value), then the individual compact card tiles.
 */
export const ActivityTradeGroup: React.FC<ActivityTradeGroupProps> = ({ group, tileWidth, valueSize, onSend }) => (
  <GlassContainer variant="cardDetails" className="activity-group">
    <div className="activity-group-header">
      <Link className="activity-group-user" to={`/profile/u/${encodeURIComponent(group.user.username)}`}>
        <AvatarWithFlag
          avatarUrl={group.user.avatarUrl}
          username={group.user.username}
          size={40}
          countryCode={group.user.countryCode}
          isElite={group.user.isElite}
          showAnimatedBorder={group.user.showAnimatedBorder}
          borderColor1={group.user.borderColor1}
          borderColor2={group.user.borderColor2}
        />
        <span className="activity-group-user-info">
          <span className="activity-group-user-name">
            {group.user.username}
            {group.user.isElite && <EliteBadge isElite size={14} />}
          </span>
          {group.user.points != null && (
            <span className="activity-group-user-points">
              <Points points={group.user.points} type="small" />
            </span>
          )}
        </span>
      </Link>
      <div className="activity-group-summary">
        <span className="activity-group-time">{formatRelativeTime(group.mostRecent)}</span>
        <div className="activity-group-totals">
          <span className="activity-group-count">
            {group.count} {group.count === 1 ? 'card' : 'cards'}
          </span>
          <Points points={group.totalPoints} type="small" />
        </div>
      </div>
    </div>

    <div className="activity-group-cards" style={{ '--tile': `${tileWidth}px` } as React.CSSProperties}>
      {group.activities.map((activity) => (
        <ActivityFeedItem key={activity.id} activity={activity} grouped valueSize={valueSize} onSend={onSend} />
      ))}
    </div>
  </GlassContainer>
);

export default ActivityTradeGroup;
